import React from "react";
import {
  ActionIcon,
  Box,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import styles from "./MobileBooking.module.css";

export interface MobileCoverPickerProps {
  /** Verb + object (addendum §4): "Select client" / "Select service" … */
  title: string;
  onClose: () => void;
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
  };
  children: React.ReactNode;
  /** Pinned under the list (the client picker's "Add a new client"). */
  footer?: React.ReactNode;
  "data-testid"?: string;
}

/**
 * Full cover of the sheet (addendum 2.5): titled header with a close X, an
 * optional search field, a scrolling list of rows and an optional pinned
 * footer.
 */
export const MobileCoverPicker: React.FC<MobileCoverPickerProps> = ({
  title,
  onClose,
  search,
  children,
  footer,
  "data-testid": testId,
}) => (
  <Box
    className={styles.cover}
    role="dialog"
    aria-label={title}
    data-testid={testId}
  >
    <Box className={styles.coverHead}>
      <Text className={styles.coverTitle}>{title}</Text>
      <ActionIcon
        variant="default"
        className={styles.iconBtn}
        onClick={onClose}
        aria-label="Close"
      >
        <IconX size={17} stroke={1.8} />
      </ActionIcon>
    </Box>
    {search && (
      <Box className={styles.coverSearch}>
        <TextInput
          value={search.value}
          onChange={(e) => search.onChange(e.currentTarget.value)}
          placeholder={search.placeholder}
          aria-label={search.placeholder}
          classNames={{ input: styles.searchInput }}
          data-autofocus
        />
      </Box>
    )}
    <Box className={styles.coverList} data-search={!!search}>
      {children}
    </Box>
    {footer && <Box className={styles.coverFoot}>{footer}</Box>}
  </Box>
);

export interface CoverRowProps {
  kind?: "service" | "staff";
  selected: boolean;
  onPick: () => void;
  /** Leading identity: swatch or avatar. */
  leading?: React.ReactNode;
  label: string;
  /** Trailing note (a service's length and price). */
  trailing?: React.ReactNode;
}

/** One 46px picker row with a check on the current value. */
export const CoverRow: React.FC<CoverRowProps> = ({
  kind,
  selected,
  onPick,
  leading,
  label,
  trailing,
}) => (
  <UnstyledButton
    className={styles.pickRow}
    data-kind={kind}
    data-on={selected}
    onClick={onPick}
  >
    {leading}
    <Box component="span" className={styles.pickName}>
      {label}
    </Box>
    {trailing}
    {selected && (
      <Box component="span" className={styles.pickTick}>
        <IconCheck size={15} stroke={1.8} />
      </Box>
    )}
  </UnstyledButton>
);
